import { prepareHostedCheckout, checkoutPlatform } from '../lib/hostedCheckout'
import { useMemberTimeZone } from '../MemberTimeZoneContext'
import { useOptionalPrivanaI18n } from '@/i18n/PrivanaI18nProvider'
import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { CalendarCheck2, CheckCircle2, Clock3, MapPin, Search, Users } from 'lucide-react'
import { useOutletContext, useParams, useSearchParams } from 'react-router-dom'
import { useMemberPortalApi } from '../api/memberPortalApiClient'
import { createMemberEventsApi } from '../api/events'
import MemberDashboardModal from '../components/MemberDashboardModal'
import MemberPaymentMethodOptions from '../components/MemberPaymentMethodOptions'

function formatEventDate(value, locale, timeZone, memberText) {
  if (!value) return memberText("portalPresentation.member_date_to_be_announced")
  const parsed = new Date(value)
  if (Number.isNaN(parsed.valueOf())) return memberText("portalPresentation.member_date_to_be_announced")
  return new Intl.DateTimeFormat(locale, { weekday: 'long', month: 'long', day: 'numeric', hour: 'numeric', minute: '2-digit', ...(timeZone ? { timeZone } : {}) }).format(parsed)
}


function priceLabel(ticketTypes = [], locale, memberText) {
  if (!ticketTypes.length) return memberText("portalPresentation.member_complimentary")
  const cents = ticketTypes.map((ticket) => Number(ticket.price_cents || 0)).filter((value) => Number.isFinite(value) && value > 0)
  if (!cents.length) return memberText("portalPresentation.member_complimentary")
  const min = Math.min(...cents)
  const max = Math.max(...cents)
  const currency = ticketTypes.find((ticket) => ticket.currency)?.currency || 'USD'
  const formatter = new Intl.NumberFormat(locale, { style: 'currency', currency })
  return min === max ? formatter.format(min / 100) : `${formatter.format(min / 100)} – ${formatter.format(max / 100)}`
}

function createGuestDraft() {
  return { first_name: '', last_name: '', email: '', phone: '', dietary_requirements: '', accessibility_needs: '', notes: '' }
}

function normalizePositiveInteger(value) {
  const parsed = Number(value)
  return Number.isInteger(parsed) && parsed > 0 ? parsed : null
}

export default function MemberEventsPage() {
  const clubTimeZone = useMemberTimeZone()
  const { t: memberText, locale } = useOptionalPrivanaI18n()

  const outlet = useOutletContext() || {}
  const params = useParams()
  const outletClubCode = String(outlet.clubCode || '').trim()
  const paramsClubCode = String(params.clubCode || '').trim()
  const resolvedClubCode = String(outlet.clubCode || params.clubCode || '').trim()
  const { memberProfile, branding } = outlet
  const timeZone = clubTimeZone || branding?.timeZone || branding?.time_zone
  const { memberPortalApiGet, memberPortalApiPost } = useMemberPortalApi()
  const eventsApi = useMemo(() => createMemberEventsApi({ memberPortalApiGet, memberPortalApiPost, clubCode: resolvedClubCode }), [memberPortalApiGet, memberPortalApiPost, resolvedClubCode])
  const [events, setEvents] = useState([])
  const [myRegistrations, setMyRegistrations] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [loadFailed, setLoadFailed] = useState(false)
  const [loadAttempt, setLoadAttempt] = useState(0)
  const pageRef = useRef(null)
  const previousScrollRange = useRef(null)
  const [query, setQuery] = useState('')
  const [activeMode, setActiveMode] = useState('upcoming')
  const [selectedEvent, setSelectedEvent] = useState(null)
  const [guestDrafts, setGuestDrafts] = useState([])
  const [notes, setNotes] = useState('')
  const [paymentParams] = useSearchParams()
  const [returnedPayment,setReturnedPayment]=useState('pending')
  const [guestPaymentMethod, setGuestPaymentMethod] = useState('member_account')
  const guestRequestRef = useRef(null), submittingRef = useRef(false)
  const [registering, setRegistering] = useState(false)
  const [successState, setSuccessState] = useState(null)
  const [confirmedAttendees, setConfirmedAttendees] = useState([])

  const mounted = useRef(false)
  const lifecycle = useRef(0)
  const successTimer = useRef(null)
  const detailRequest = useRef(0)
  useEffect(() => {
    mounted.current = true
    lifecycle.current++
    return () => {
      mounted.current = false
      lifecycle.current++
      detailRequest.current++
      window.clearTimeout(successTimer.current)
    }
  }, [resolvedClubCode])

  useEffect(() => {
    console.info('[MemberEventsPage] mount context', {
      outletClubCode,
      paramsClubCode,
      resolvedClubCode,
    })
  }, [outletClubCode, paramsClubCode, resolvedClubCode])

  useEffect(() => {
    if (!successState) return undefined
    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        event.preventDefault()
        event.stopPropagation()
      }
    }
    window.addEventListener('keydown', handleKeyDown, true)
    return () => window.removeEventListener('keydown', handleKeyDown, true)
  }, [successState])

  useEffect(() => {
    let active = true
    const load = async () => {
      setLoading(true)
      setLoadFailed(false)
      setError('')
      try {
        console.info('[MemberEventsPage] calling listEvents()', {
          clubCode: resolvedClubCode,
          path: `/api/members-portal/${resolvedClubCode}/events`,
        })
        const [eventsData, registrationsData] = await Promise.all([eventsApi.listEvents(), eventsApi.listMyRegistrations()])
        console.info('[MemberEventsPage] listEvents() resolved', {
          clubCode: resolvedClubCode,
          count: Array.isArray(eventsData) ? eventsData.length : 0,
          ids: Array.isArray(eventsData) ? eventsData.map((event) => event?.id).filter(Boolean) : [],
          path: `/api/members-portal/${resolvedClubCode}/events`,
        })
        if (!active) return
        setEvents(Array.isArray(eventsData) ? eventsData : [])
        setMyRegistrations(Array.isArray(registrationsData) ? registrationsData : [])
      } catch (err) {
        console.error('[MemberEventsPage] listEvents() failed', {
          clubCode: resolvedClubCode,
          path: `/api/members-portal/${resolvedClubCode}/events`,
          error: err,
        })
        if (active) {
          setLoadFailed(true)
          setError("portalPresentation.member_unable_to_load_events")
        }
      } finally {
        if (active) setLoading(false)
      }
    }
    if (!resolvedClubCode) {
      setLoading(false)
      return () => { active = false }
    }
    load()
    return () => { active = false }
  }, [resolvedClubCode, eventsApi, loadAttempt])

  const scopedPublishedEvents = useMemo(() => {
    const now = Date.now()
    return events.filter((event) => {
      const eventClubCode = String(event?.club_code || '').trim()
      const eventStatus = String(event?.status || '').toLowerCase()
      const endAtMs = new Date(event?.end_at || event?.start_at || 0).valueOf()
      const isCancelled = Boolean(event?.cancelled_at)
      const matchesClub = eventClubCode && eventClubCode === resolvedClubCode
      const isPublished = eventStatus === 'published'
      const hasValidEndAt = Number.isFinite(endAtMs)
      if (!matchesClub || !isPublished || isCancelled || !hasValidEndAt) return false
      return activeMode === 'past' ? endAtMs < now : endAtMs >= now
    })
  }, [events, resolvedClubCode, activeMode])

  const visibleEvents = useMemo(() => {
    const sorted = [...scopedPublishedEvents].sort((left, right) => {
      const leftStart = new Date(left?.start_at || left?.end_at || 0).valueOf()
      const rightStart = new Date(right?.start_at || right?.end_at || 0).valueOf()
      return activeMode === 'past' ? rightStart - leftStart : leftStart - rightStart
    })
    const normalized = query.trim().toLowerCase()
    if (!normalized) return sorted
    return sorted.filter((event) => [event.title, event.description, event.location, event.event_type].join(' ').toLowerCase().includes(normalized))
  }, [scopedPublishedEvents, query, activeMode])

  // Loading cards can disappear during a native gesture. Reconcile a retained
  // offset only when it exceeds a smaller extent. Retries, filters and
  // responses must preserve every valid offset.
  useLayoutEffect(() => {
    const scrollport = pageRef.current?.closest('.mp-main')
    if (!scrollport || !/auto|scroll/.test(getComputedStyle(scrollport).overflowY)) return
    const range = Math.max(0, scrollport.scrollHeight - scrollport.clientHeight)
    if (previousScrollRange.current != null && range < previousScrollRange.current && scrollport.scrollTop > range) {
      scrollport.scrollTop = range
    }
    previousScrollRange.current = range
  }, [loading, error, visibleEvents, myRegistrations])

  const openEvent = async (eventId) => {
    const request = ++detailRequest.current
    try {
      const data = await eventsApi.getEvent(eventId)
      if (!mounted.current || request !== detailRequest.current) return
      setSelectedEvent(data)
      setGuestDrafts([])
      setNotes('')
      setGuestPaymentMethod('member_account')
      setSuccessState(null)
      setConfirmedAttendees([])
    } catch (err) {
      if (mounted.current && request === detailRequest.current) setError("portalPresentation.member_unable_to_load_event_details")
    }
  }

  const updateGuest = (index, field, value) => {
    setGuestDrafts((current) => current.map((guest, guestIndex) => (guestIndex === index ? { ...guest, [field]: value } : guest)))
  }

  const addGuest = () => {
    const maxGuests = normalizePositiveInteger(selectedEvent?.event?.max_guests_per_registration)
    setGuestDrafts((current) => (maxGuests != null && current.length >= maxGuests ? current : [...current, createGuestDraft()]))
  }

  const removeGuest = (index) => {
    setGuestDrafts((current) => current.filter((_, guestIndex) => guestIndex !== index))
  }

  useEffect(()=>{
    const refresh=()=>{setLoadAttempt(value=>value+1);if(selectedEvent?.event?.id)eventsApi.getEvent(selectedEvent.event.id).then(setSelectedEvent).catch(()=>{})}
    window.addEventListener('members:checkout-closed',refresh);window.addEventListener('focus',refresh)
    return()=>{window.removeEventListener('members:checkout-closed',refresh);window.removeEventListener('focus',refresh)}
  },[eventsApi,selectedEvent?.event?.id])
  useEffect(()=>{
    const session=paymentParams.get('session_id')
    if(!session || paymentParams.get('event_payment')!=='success')return
    let active=true
    const check=async()=>{try{const res=await memberPortalApiGet(`/api/members-portal/${resolvedClubCode}/booking-payments/status?session_id=${encodeURIComponent(session)}`);const data=await res.json();if(active && res.ok){setReturnedPayment(data.status);setLoadAttempt(v=>v+1)}}catch{/* Keep the authoritative outcome pending until a successful read. */}}
    check();const timer=window.setInterval(check,3000);return()=>{active=false;window.clearInterval(timer)}
  },[paymentParams,memberPortalApiGet,resolvedClubCode])

  const resumeEventPayment=async()=>{
    if(!selectedEvent?.myRegistration || submittingRef.current)return
    submittingRef.current=true;setRegistering(true);setError('')
    const checkout=prepareHostedCheckout(`/privana-pay/checkout-opening?lang=${encodeURIComponent(locale)}`)
    try{
      const response=await memberPortalApiPost(`/api/members-portal/${resolvedClubCode}/events/${selectedEvent.event.id}/registrations/${selectedEvent.myRegistration.id}/checkout`,{platform:checkoutPlatform()})
      const data=await response.json()
      if(!response.ok || data.ok===false)throw new Error('checkout_failed')
      if(data.status==='completed'){checkout.close();setSelectedEvent(await eventsApi.getEvent(selectedEvent.event.id));return}
      if(!data.url)throw new Error('checkout_failed')
      await checkout.location.assign(data.url)
    }catch{checkout.close();setError('memberAccount.checkoutFailed')}
    finally{submittingRef.current=false;setRegistering(false)}
  }
  const handleReserve = async () => {
    if (!selectedEvent || submittingRef.current) return
    const invalidGuest = guestDrafts.find((guest) => !guest.first_name.trim() || !guest.last_name.trim())
    if (invalidGuest) {
      setError("portalPresentation.member_each_guest_needs_a_first_and_last_name")
      return
    }
    const currentLifecycle = lifecycle.current
    const isCurrent = () => mounted.current && currentLifecycle === lifecycle.current
    submittingRef.current = true
    setRegistering(true)
    setError('')
    let checkoutWindow = null
    try {
      const ticketTypes = selectedEvent.ticketTypes || []
      const memberTicket = ticketTypes.find((ticket) => String(ticket.name || '').trim().toLowerCase() === 'member') || ticketTypes[0] || null
      const guestTicket = ticketTypes.find((ticket) => String(ticket.name || '').trim().toLowerCase() === 'guest') || (ticketTypes.length === 1 ? ticketTypes[0] : null)
      const existingRegistration = selectedEvent.myRegistration || null
      const guestFingerprint = JSON.stringify([selectedEvent.event.id,existingRegistration?.id,guestDrafts,guestPaymentMethod])
      if(guestRequestRef.current?.fingerprint!==guestFingerprint)guestRequestRef.current={fingerprint:guestFingerprint,key:crypto.randomUUID()}
      if(existingRegistration && guestPaymentMethod==='pay_now')checkoutWindow=prepareHostedCheckout(`/privana-pay/checkout-opening?lang=${encodeURIComponent(locale)}`)
      const registrationResult = existingRegistration
        ? await eventsApi.addGuests(selectedEvent.event.id, existingRegistration.id, { guests: guestDrafts, payment_method: guestPaymentMethod, request_key: guestRequestRef.current.key, platform: checkoutPlatform() })
        : await eventsApi.register(selectedEvent.event.id, {
        ticket_type_id: memberTicket?.id || null,
        member_ticket_type_id: memberTicket?.id || null,
        guest_ticket_type_id: guestDrafts.length ? (guestTicket?.id || null) : null,
        attendee_first_name: memberProfile?.first_name || '',
        attendee_last_name: memberProfile?.last_name || '',
        attendee_email: memberProfile?.email || '',
        attendee_phone: memberProfile?.phone_number || '',
        notes,
        guests: guestDrafts,
      })
      if (!isCurrent()) return
      if (existingRegistration && guestPaymentMethod === 'pay_now' && registrationResult?.checkout?.url) {
        setGuestDrafts([]);setSelectedEvent(await eventsApi.getEvent(selectedEvent.event.id))
        await checkoutWindow.location.assign(registrationResult.checkout.url)
        return
      }
      if(existingRegistration && guestPaymentMethod==='pay_now' && registrationResult?.checkout?.error)throw new Error('checkout_failed')
      checkoutWindow?.close()
      setSuccessState({ eventName: selectedEvent.event.title, startAt: selectedEvent.event.start_at, timeZone: selectedEvent.event.time_zone || selectedEvent.event.timezone || timeZone, location: selectedEvent.event.location })
      setConfirmedAttendees(Array.isArray(registrationResult?.attendees) ? registrationResult.attendees : [])
      setGuestDrafts([])
      setNotes('')
      setGuestPaymentMethod('member_account')
      successTimer.current = window.setTimeout(async () => {
        setSelectedEvent(null)
        setSuccessState(null)
        setConfirmedAttendees([])
        try {
          const [eventsData, registrationsData] = await Promise.all([eventsApi.listEvents(), eventsApi.listMyRegistrations()])
          if (!isCurrent()) return
          setEvents(Array.isArray(eventsData) ? eventsData : [])
          setMyRegistrations(Array.isArray(registrationsData) ? registrationsData : [])
        } catch {
          if (isCurrent()) setError("portalPresentation.member_unable_to_load_events")
        }
      }, 3000)
    } catch (err) {
      checkoutWindow?.close()
      if (isCurrent()) setError("portalPresentation.member_registration_failed")
    } finally {
      submittingRef.current=false
      if (isCurrent()) setRegistering(false)
    }
  }

  const canShowAddGuestForEvent = (event, registration) => {
    if (!registration || !event?.allow_guests) return false
    const eventStatus = String(event?.status || '').toLowerCase()
    if (eventStatus === 'cancelled' || event?.cancelled_at) return false
    const endAtMs = new Date(event?.end_at || event?.start_at || 0).valueOf()
    if (Number.isFinite(endAtMs) && endAtMs < Date.now()) return false
    const maxGuests = normalizePositiveInteger(event?.max_guests_per_registration)
    const existingGuests = Number(registration?.guest_count ?? registration?.guests_count ?? Math.max(Number(registration?.quantity || 1) - 1, 0))
    if (maxGuests != null && existingGuests >= maxGuests) return false
    const capacity = Number(event?.capacity || 0)
    const attendeeCount = Number(event?.attendees_count || event?.attendees || 0)
    if (capacity > 0 && attendeeCount >= capacity) return false
    return true
  }

  return (
    <div className="mp-events-page" ref={pageRef}>
      {paymentParams.get('event_payment') ? <p role="status">{memberText(returnedPayment==='completed'?'portalPresentation.member_paid_online_thank_you':paymentParams.get('event_payment')==='cancelled'?'facilityBooking.checkoutCancelledHelp':'memberAccount.settlementPending')}</p> : null}
      <section className="mp-events-hero">
<div className="mp-events-mobile-heading"><h1>{memberText("portalPresentation.member_events_heading")}</h1><span className="mp-events-stat">{memberText(scopedPublishedEvents.length === 1 ? "portalPresentation.member_event_count_one" : "portalPresentation.member_event_count_other", { count: scopedPublishedEvents.length })}</span></div>
        <div className="mp-events-hero__copy">
          <p className="mp-events-hero__headline">{memberText("portalPresentation.member_explore_exclusive_events")}</p>
        </div>
        <div className="mp-events-hero__meta">
          <div className="mp-events-stat"><Users size={16} /> {memberText(scopedPublishedEvents.length === 1 ? "portalPresentation.member_event_count_one" : "portalPresentation.member_event_count_other", { count: scopedPublishedEvents.length })}</div>
        </div>
      </section>

      <section className="mp-events-toolbar">
        <label className="mp-events-search">
          <Search size={16} />
          <input aria-label={memberText("portalPresentation.member_search_titles_venues_or_occasion_type")} value={query} onChange={(event) => setQuery(event.target.value)} placeholder={memberText("portalPresentation.member_search_titles_venues_or_occasion_type")} />
        </label>
        <div className="mp-events-mode-toggle" role="tablist" aria-label={memberText("portalPresentation.member_event_timeframe")}>
          <button type="button" className={`mp-secondary ${activeMode === 'upcoming' ? 'is-active' : ''}`} role="tab" aria-selected={activeMode === 'upcoming'} onClick={() => setActiveMode('upcoming')}>{memberText("portalPresentation.member_upcoming_caption")}</button>
          <button type="button" className={`mp-secondary ${activeMode === 'past' ? 'is-active' : ''}`} role="tab" aria-selected={activeMode === 'past'} onClick={() => setActiveMode('past')}>{memberText("portalPresentation.member_past_events")}</button>
        </div>
      </section>

      {loading ? (
        <div className="mp-events-grid">{Array.from({ length: 3 }).map((_, index) => <div key={index} className="mp-event-card mp-event-card--skeleton" />)}</div>
      ) : error ? (
        <div className="mp-events-empty" role="alert">
          <p>{memberText(error)}</p>
          {loadFailed ? <button type="button" className="mp-secondary" onClick={() => setLoadAttempt((attempt) => attempt + 1)}>{memberText("portalPresentation.member_events_retry")}</button> : null}
        </div>
      ) : visibleEvents.length === 0 ? (
        <div className="mp-events-empty">
          {query.trim()
            ? memberText("portalPresentation.member_no_events_match_your_search_right_now")
            : activeMode === 'past'
              ? memberText("portalPresentation.member_no_past_events_just_yet")
              : memberText("portalPresentation.member_no_upcoming_events_right_now_please_check_back_soon")}
        </div>
      ) : (
        <div className="mp-events-grid">
          {visibleEvents.map((event) => {
            const tickets = Array.isArray(event.ticket_types || event.ticketTypes) ? (event.ticket_types || event.ticketTypes) : []
            const capacity = Number(event.capacity || 0)
            const attendees = Number(event.attendees_count || 0)
            const availability = capacity > 0 ? memberText("portalPresentation.member__count_of_capacity_places_remaining", { count: Math.max(capacity - attendees, 0), capacity }) : memberText("portalPresentation.member_availability_on_request")
            const isPastEvent = activeMode === 'past'
            const registration = myRegistrations.find((reg) => String(reg.event_id) === String(event.id) && !['cancelled', 'canceled', 'void', 'refunded'].includes(String(reg.status || '').toLowerCase()))
            const isRegistered = Boolean(registration)
            const canAddGuest = canShowAddGuestForEvent(event, registration)
            return (
              <article key={event.id} className="mp-event-card">
                <div className="mp-event-card__image" style={{ backgroundImage: event.cover_image_url ? `linear-gradient(180deg, rgba(0,0,0,0.12), rgba(0,0,0,0.55)), url(${event.cover_image_url})` : undefined }} />
                <div className="mp-event-card__body">
                  <div className="mp-event-card__header">
                    <span className="mp-event-pill">{event.event_type || memberText("portalPresentation.member_event")}</span>
                    <span className="mp-event-pill">{priceLabel(tickets, locale, memberText)}</span>
                  </div>
                  <h3>{event.title}</h3>
                  <p>{event.description || memberText("portalPresentation.member_more_details_will_be_announced_shortly")}</p>
                  <div className="mp-event-meta"><Clock3 size={14} /> {formatEventDate(event.start_at, locale, event.time_zone || event.timezone || timeZone, memberText)}</div>
                  <div className="mp-event-meta"><MapPin size={14} /> {event.location || memberText("portalPresentation.member_club_venue")}</div>
                  <div className="mp-event-meta"><Users size={14} /> {availability}</div>
                  {registration?.has_pending_online_payment ? <button type="button" className="mp-primary" onClick={()=>openEvent(event.id)}>{memberText('portalPresentation.member_pay_securely_with_privana_pay')}</button> : null}
                  {!isPastEvent ? (
                    <div className="mp-event-card__actions">
                      <button type="button" className={isRegistered ? 'mp-reserved-button' : 'mp-primary'} disabled={isRegistered} onClick={() => openEvent(event.id)}><CalendarCheck2 size={16} /> {isRegistered ? memberText("portalPresentation.member_reserved") : memberText("portalPresentation.member_reserve")}</button>
                      {canAddGuest ? <button type="button" className="mp-primary" onClick={() => openEvent(event.id)}>{memberText("portalPresentation.member_add_guest")}</button> : null}
                    </div>
                  ) : null}
                </div>
              </article>
            )
          })}
        </div>
      )}

      {selectedEvent ? (
        <MemberDashboardModal labelledBy="member-event-title" testId="member-event-modal" size="dashboard"
          onClose={() => setSelectedEvent(null)} closeLabel={memberText("portalPresentation.member_close")}
          closeOnBackdrop={!successState} closeOnEscape={!successState} showCloseButton={!successState}
          bodyClassName="mp-event-dialog-scroll" screenKey={successState ? 'confirmed' : selectedEvent.event.id}>
          <div>
            {!successState && <div className="mp-event-modal__hero" style={{ backgroundImage: selectedEvent.event.cover_image_url ? `linear-gradient(180deg, rgba(0,0,0,0.08), rgba(0,0,0,0.68)), url(${selectedEvent.event.cover_image_url})` : undefined }}>
              <div>
                <p className="mp-eyebrow">{selectedEvent.event.event_type || memberText("portalPresentation.member_signature_event")}</p>
                <h2 id="member-event-title">{selectedEvent.event.title}</h2>
                <p>{selectedEvent.event.description || memberText("portalPresentation.member_an_exceptional_club_gathering")}</p>
              </div>
            </div>}
            <div className="mp-event-modal__content">
              {successState ? (
                <div className="mp-events-success-state">
                  <div className="mp-events-success-state__mark"><CheckCircle2 size={56} /></div>
                  <h3 id="member-event-title">{memberText("portalPresentation.member_you_re_in")}</h3>
                  <p className="mp-events-success-state__event">{successState.eventName}</p>
                  <p>{formatEventDate(successState.startAt, locale, successState.timeZone, memberText)}</p>
                  <p>{successState.location || memberText("portalPresentation.member_club_venue")}</p>
                </div>
              ) : (<>
              <div className="mp-event-meta"><Clock3 size={14} /> {formatEventDate(selectedEvent.event.start_at, locale, selectedEvent.event.time_zone || selectedEvent.event.timezone || timeZone, memberText)}</div>
              <div className="mp-event-meta"><MapPin size={14} /> {selectedEvent.event.location || memberText("portalPresentation.member_club_venue")}</div>
              <div className="mp-event-meta"><Users size={14} /> {selectedEvent.event.allow_guests ? (selectedEvent.event.max_guests_per_registration ? memberText("portalPresentation.member_guests_allowed_up_to_count", { count: selectedEvent.event.max_guests_per_registration }) : memberText("portalPresentation.member_guests_allowed")) : memberText("portalPresentation.member_guests_not_permitted")}</div>
              <div className="mp-event-ticket-list">
                {(selectedEvent.ticketTypes || []).map((ticket) => <div key={ticket.id} className="mp-event-ticket-row"><strong>{ticket.name}</strong><span>{priceLabel([ticket], locale, memberText)}</span></div>)}
              </div>
              {selectedEvent.event.allow_guests ? (
                <div className="mp-events-guests">
                  <div className="mp-events-guests__header">
                    <strong>{selectedEvent.myRegistration ? memberText("racquetPlay.addGuest") : memberText("portalPresentation.member_guest_details")}</strong>
                    <button type="button" className="mp-secondary" onClick={addGuest}>{memberText("racquetPlay.addGuest")}</button>
                  </div>
                  <div className="mp-events-guests__list">
                  {guestDrafts.map((guest, index) => (
                    <div key={index} className="mp-events-guest-card">
                      <div className="mp-events-guest-grid">
                        <label className="mp-events-field"><span>{memberText("portalPresentation.member_first_name")}</span><input value={guest.first_name} onChange={(event) => updateGuest(index, 'first_name', event.target.value)} /></label>
                        <label className="mp-events-field"><span>{memberText("portalPresentation.member_last_name")}</span><input value={guest.last_name} onChange={(event) => updateGuest(index, 'last_name', event.target.value)} /></label>
                        <label className="mp-events-field"><span>{memberText("portalPresentation.member_email")}</span><input value={guest.email} onChange={(event) => updateGuest(index, 'email', event.target.value)} /></label>
                        <label className="mp-events-field"><span>{memberText("portalPresentation.member_phone")}</span><input value={guest.phone} onChange={(event) => updateGuest(index, 'phone', event.target.value)} /></label>
                        <label className="mp-events-field"><span>{memberText("portalPresentation.member_dietary")}</span><input value={guest.dietary_requirements} onChange={(event) => updateGuest(index, 'dietary_requirements', event.target.value)} /></label>
                        <label className="mp-events-field"><span>{memberText("portalPresentation.member_accessibility")}</span><input value={guest.accessibility_needs} onChange={(event) => updateGuest(index, 'accessibility_needs', event.target.value)} /></label>
                      </div>
                      <label className="mp-events-field"><span>{memberText("portalPresentation.member_notes")}</span><textarea value={guest.notes} onChange={(event) => updateGuest(index, 'notes', event.target.value)} /></label>
                      <button type="button" className="mp-secondary" onClick={() => removeGuest(index)}>{memberText("portalPresentation.member_remove_guest")}</button>
                    </div>
                  ))}
                  </div>
                  {selectedEvent.myRegistration && guestDrafts.length ? <MemberPaymentMethodOptions value={guestPaymentMethod} onChange={setGuestPaymentMethod} includeGuestPay disabled={registering} /> : null}
                </div>
              ) : null}
              {selectedEvent.myRegistration?.attendees?.some(a=>a.payer_type==='member' && a.payment_method==='online' && a.payment_status==='awaiting_payment') ? <button type="button" className="mp-primary" disabled={registering} onClick={resumeEventPayment}>{memberText('portalPresentation.member_pay_securely_with_privana_pay')}</button> : null}
              <label className="mp-events-field"><span>{memberText("portalPresentation.member_attendance_notes")}</span><textarea value={notes} onChange={(event) => setNotes(event.target.value)} placeholder={memberText("portalPresentation.member_dietary_needs_accessibility_notes_or_concierge_requests")} /></label>
              {confirmedAttendees.length ? (
                <div className="mp-events-guests">
                  <strong>{memberText("portalPresentation.member_confirmed_attendance")}</strong>
                  {confirmedAttendees.map((attendee) => (
                    <div key={attendee.id || `${attendee.first_name}-${attendee.last_name}`} className="mp-event-ticket-row"><strong>{[attendee.first_name, attendee.last_name].filter(Boolean).join(' ') || memberText("portalPresentation.member_guest")}</strong><span>{attendee.is_guest ? memberText("portalPresentation.member_guest") : memberText("portalPresentation.member_member")}</span></div>
                  ))}
                </div>
              ) : null}
              {error ? <p role="alert">{memberText(error)}</p> : null}
              <button type="button" className="mp-primary" disabled={registering} onClick={handleReserve}>{registering ? memberText("portalPresentation.confirming") : (selectedEvent.myRegistration ? memberText("portalPresentation.member_add_guest") : memberText("portalPresentation.member_confirm_attendance"))}</button>
              </>)}
            </div>
          </div>
        </MemberDashboardModal>
      ) : null}
    </div>
  )
}
